'use client';

import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import {
  CheckIcon,
  UserIcon,
  UsersIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { SetFormValues } from '@components/common/Forms';
import { SetMirrorData } from '@type/job-add';
import {
  getGuaranteedFirstDayApplicants,
  resolveJobAddV2BundleKey as resolveBundleKey,
} from '@components/templates/job-add-v2/utils/bundleType';

const BUNDLE_ORDER: Record<NonNullable<ReturnType<typeof resolveBundleKey>>, number> = {
  freemium: 0,
  agency: 1,
  hire: 2,
  pro: 3,
  plus: 4,
  standard: 5,
  live: 6,
};

export const isBundleSupportedInJobAddV2 = (bundle?: ApiSchema<'ActivationBundle'>): boolean => {
  if (!bundle) return false;
  const bundleKey = resolveBundleKey(bundle);
  if (!bundleKey) return false;
  return bundleKey !== 'live' && bundleKey !== 'agency';
};

const toPrice = (value?: number): number | undefined => {
  if (typeof value !== 'number' || Number.isNaN(value)) return undefined;
  return Math.ceil(value);
};

const getBundleCurrentPrice = (bundle?: ApiSchema<'ActivationBundle'>): number | undefined =>
  toPrice(bundle?.discountedCredits) ?? toPrice(bundle?.credits);

const getBundleOrder = (bundle?: ApiSchema<'ActivationBundle'>): number => {
  const bundleKey = resolveBundleKey(bundle);
  return bundleKey ? BUNDLE_ORDER[bundleKey] : 99;
};

export const sortSupportedBundlesForJobAddV2 = (bundles: ApiSchema<'ActivationBundle'>[]): ApiSchema<'ActivationBundle'>[] =>
  bundles
    .filter((bundle) => isBundleSupportedInJobAddV2(bundle))
    .sort((a, b) => {
      const priceDiff = (getBundleCurrentPrice(a) ?? Number.POSITIVE_INFINITY) - (getBundleCurrentPrice(b) ?? Number.POSITIVE_INFINITY);
      if (priceDiff !== 0) return priceDiff;

      return getBundleOrder(a) - getBundleOrder(b);
    });

export const getDisplayBundlesForJobAddV2 = (bundles: ApiSchema<'ActivationBundle'>[]): ApiSchema<'ActivationBundle'>[] =>
  sortSupportedBundlesForJobAddV2(bundles);

type BundleTitleTranslator = (key: string, options?: Record<string, unknown>) => string;

export const getBundleDisplayTitleV2 = (bundle: ApiSchema<'ActivationBundle'> | undefined, t: BundleTitleTranslator): string => {
  const bundleKey = resolveBundleKey(bundle);

  if (bundleKey === 'live') return bundle?.title || t('job.bundle.name.live') || 'Live';
  if (bundleKey === 'plus' || bundleKey === 'pro') return bundle?.title || t('job.add.extra.promo.agent');

  return bundle?.title || t('job.add.extra.promo.agent');
};

const getBundleShortTitleV2 = (bundle: ApiSchema<'ActivationBundle'> | undefined, t: BundleTitleTranslator): string => {
  const bundleKey = resolveBundleKey(bundle);
  if (bundleKey === 'freemium') return t('standalone.freemium.bundle.name');

  const displayTitle = getBundleDisplayTitleV2(bundle, t);
  const parenthesizedTitle = displayTitle.match(/\(([^)]+)\)/)?.[1]?.trim();
  if (parenthesizedTitle) return parenthesizedTitle;

  if (bundleKey === 'hire') return 'Hire';
  if (bundleKey === 'pro') return 'Pro';
  if (bundleKey === 'plus') return 'Plus';
  if (bundleKey === 'standard') return 'Start';

  return displayTitle;
};

const getBundlePriceData = (
  bundle?: ApiSchema<'ActivationBundle'>,
): {
  oldPrice?: number;
  currentPrice?: number;
} => {
  const oldPrice = toPrice(bundle?.credits);
  const discountedPrice = toPrice(bundle?.discountedCredits);
  const currentPrice = discountedPrice ?? oldPrice;

  if (typeof currentPrice !== 'number') {
    return {};
  }

  if (typeof oldPrice === 'number' && oldPrice > currentPrice) {
    return { oldPrice, currentPrice };
  }

  return { currentPrice };
};

export const getBundleCtaTextV2 = (bundle: ApiSchema<'ActivationBundle'> | undefined, t: BundleTitleTranslator): string => {
  const title = getBundleShortTitleV2(bundle, t);
  const { currentPrice } = getBundlePriceData(bundle);

  if (typeof currentPrice === 'number') {
    return `${title} - ${currentPrice} ${t('global.label.euro')}`;
  }

  return title;
};

interface ActivationBundleCardV2Props {
  bundle: ApiSchema<'ActivationBundle'>;
  isSelected: boolean;
  disabled?: boolean;
  onSelect: (bundle: ApiSchema<'ActivationBundle'>) => void;
  t: BundleTitleTranslator;
  /**
   * Mirrors the parent tab choice. True = Recrutezi tu (no-hire siblings) —
   * card skips the dedicated-recruiter decorations and the "AVANS LA
   * ACTIVARE" / commission disclaimers that only apply when Hire is bundled.
   */
  withoutHire?: boolean;
}

const getBundleTierIcon = (bundle: ApiSchema<'ActivationBundle'> | undefined) => {
  const bundleKey = resolveBundleKey(bundle);
  if (bundleKey === 'standard') return UserIcon;
  if (bundleKey === 'plus') return UsersIcon;
  if (bundleKey === 'pro') return UserGroupIcon;

  return null;
};

const getBundleSubtitleV2 = (bundle: ApiSchema<'ActivationBundle'> | undefined, t: BundleTitleTranslator): string => {
  const bundleKey = resolveBundleKey(bundle);
  if (bundleKey === 'freemium') return t('mc.services.bundle.freemium.mainbenefit');
  if (bundleKey === 'agency') return t('mc.services.bundle.agency.subtitle');
  if (bundleKey === 'hire') return t('mc.services.bundle.hire.mainbenefit');
  if (bundleKey === 'pro') return t('mc.services.bundle.pro.mainbenefit');
  if (bundleKey === 'plus') return t('mc.services.bundle.plus.mainbenefit');
  if (bundleKey === 'standard') return t('mc.services.bundle.start_view.subtitle');

  return bundle?.subtitle || '';
};

/**
 * Optional second bullet rendered under the subtitle. Only Plus / Pro / Hire
 * cards have a second point today; Start / other bundles return '' and the
 * card renders one bullet.
 */
const getBundleBullet2V2 = (bundle: ApiSchema<'ActivationBundle'> | undefined, t: BundleTitleTranslator): string => {
  const bundleKey = resolveBundleKey(bundle);
  if (bundleKey === 'standard') return t('mc.services.bundle.standard.subtitle.bullet2');
  if (bundleKey === 'hire') return t('mc.services.bundle.hire.subtitle.bullet2');
  if (bundleKey === 'pro') return t('mc.services.bundle.pro.subtitle.bullet2');
  if (bundleKey === 'plus') return t('mc.services.bundle.plus.subtitle.bullet2');
  if (bundleKey === 'freemium') return t('mc.services.bundle.freemium.benefit');

  return '';
};

/**
 * First bullet: guaranteed applicants in the first day. Only Start / Plus / Pro
 * carry a guarantee; other bundles return '' and the bullet is not rendered.
 */
const getBundleGuaranteedApplicantsV2 = (bundle: ApiSchema<'ActivationBundle'> | undefined, t: BundleTitleTranslator): string => {
  const applicants = getGuaranteedFirstDayApplicants(resolveBundleKey(bundle));
  if (!applicants) return '';

  return t('mc.services.bundle.guaranteed_first_day_applicants', { applicants });
};

const ActivationBundleCardV2 = (props: ActivationBundleCardV2Props) => {
  const {
    bundle, isSelected, disabled = false, onSelect, t,
    withoutHire = false,
  } = props;
  const { oldPrice, currentPrice } = getBundlePriceData(bundle);
  const subtitle = getBundleSubtitleV2(bundle, t);
  const bullet2 = getBundleBullet2V2(bundle, t);
  const guaranteedApplicants = getBundleGuaranteedApplicantsV2(bundle, t);
  const TierIcon = getBundleTierIcon(bundle);
  const bundleKey = resolveBundleKey(bundle);
  // Hire-only decorations: AVANS/commission disclaimers + the dedicated-recruiter
  // top bullet only make sense when the Hire service is bundled, i.e. on the
  // Recrutam-pentru-tine tab. Freemium never gets them either.
  const showHireDecorations = !withoutHire && bundleKey !== 'freemium';
  const isFreeBundle = currentPrice === 0;
  // Independent of the price wording: a bundle discounted down to zero — by a
  // subscription that covers it, say — still needs to show what it would have
  // cost, otherwise "gratuit" alone gives no sense of the benefit. The freemium
  // bundle has no higher list price, so nothing is struck through there.
  const hasListPrice = typeof oldPrice === 'number' && oldPrice > (currentPrice ?? oldPrice);

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(bundle)}
      className={`flex h-full min-h-48 w-full flex-col rounded-2xl border bg-surface p-5 text-left transition-colors ${isSelected ? 'border-secondary bg-secondary/5 ring-2 ring-secondary/20' : 'border-input/70 hover:border-secondary/50'} ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          {TierIcon && <TierIcon className="mt-0.5 size-5 shrink-0 stroke-2 text-secondary" />}
          <div className="text-xl font-bold leading-tight text-ink">{getBundleShortTitleV2(bundle, t)}</div>
        </div>
        <span
          aria-hidden="true"
          className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2 ${isSelected ? 'border-secondary' : 'border-input'}`}
        >
          {isSelected && <span className="size-2.5 rounded-full bg-secondary" />}
        </span>
      </div>

      {showHireDecorations && (
        <div className="mt-4 flex items-start gap-2 rounded-xl bg-secondary/5 p-2 text-sm font-semibold text-secondary">
          <SparklesIcon className="mt-0.5 size-4 shrink-0 text-secondary" />
          <span>{t('job.bundle.selection.recruiter_dedicated')}</span>
        </div>
      )}

      {guaranteedApplicants && (
        <div className={`${showHireDecorations ? 'mt-3' : 'mt-4'} flex items-start gap-2 text-sm text-ink-medium`}>
          <CheckIcon className="mt-0.5 size-4 shrink-0 stroke-2 text-secondary" />
          <span>{guaranteedApplicants}</span>
        </div>
      )}

      <div className={`${guaranteedApplicants ? 'mt-2' : (showHireDecorations ? 'mt-3' : 'mt-4')} flex items-start gap-2 text-sm text-ink-medium`}>
        <CheckIcon className="mt-0.5 size-4 shrink-0 stroke-2 text-secondary" />
        <span>{subtitle}</span>
      </div>

      {bullet2 && (
        <div className="mt-2 flex items-start gap-2 text-sm text-ink-medium">
          <CheckIcon className="mt-0.5 size-4 shrink-0 stroke-2 text-secondary" />
          <span>{bullet2}</span>
        </div>
      )}

      <div className="mt-auto flex flex-col items-end gap-1 pt-4">
        <div className="flex flex-wrap items-baseline justify-end gap-x-2 gap-y-1 text-lg font-semibold text-secondary">
          {hasListPrice && (
            <span className="text-sm font-normal text-ink-medium line-through">
              {oldPrice} {t('employer.branding.price.desc')}
            </span>
          )}

          {isFreeBundle ? (
            <span>{t('mc.services.bundle.freemium.price')}</span>
          ) : (
            <span>
              {typeof currentPrice === 'number' ? currentPrice : 0} {t('employer.branding.price.desc')}
            </span>
          )}
        </div>

        {showHireDecorations && (
          // Shown with strikethrough: the limited-time promo banner above
          // promises the commission is on us. The crossed-out line visually
          // reinforces "you don't pay this today" while still disclosing
          // the number so the client sees the full price shape.
          <span className="text-xs text-ink-medium line-through">
            {t('job.bundle.selection.price.commission_at_hire')}
          </span>
        )}
      </div>
    </button>
  );
};

interface JobBundleSelectionSectionV2Props {
  setFormValues: SetFormValues;
  setMirrorData?: SetMirrorData;
  bundles?: ApiSchema<'ActivationBundle'>[];
  selectedBundleId?: string;
  inActivePeriod?: boolean;
  /** Highlights the section after an activation attempt with nothing picked. */
  hasMissingSelectionError?: boolean;
  /**
   * Current Alege-cum-recrutezi tab. When true the user is on "Recrutezi tu"
   * (self-serve siblings, 1.5x price, no Hire addon); when false they are on
   * the default "Recrutăm pentru tine" tab (legacy with-hire set).
   */
  withoutHire?: boolean;
}

export const JobBundleSelectionSectionV2 = (props: JobBundleSelectionSectionV2Props) => {
  const {
    setFormValues, setMirrorData, bundles = [], selectedBundleId, inActivePeriod, hasMissingSelectionError = false,
    withoutHire = false,
  } = props;

  const { t } = useTranslation('common');

  const availableBundles = useMemo(
    () => sortSupportedBundlesForJobAddV2(bundles).filter((bundle) => bundle.available),
    [bundles],
  );

  // Freemium is no longer offered as a card — it is what the client gets by
  // deferring the choice, via the checkbox below. It stays in the bundle list
  // though: the checkbox still submits its packageId, which is what makes the
  // backend activate the job as freemium.
  const freemiumBundle = useMemo(
    () => availableBundles.find((bundle) => resolveBundleKey(bundle) === 'freemium'),
    [availableBundles],
  );
  const paidBundles = useMemo(
    () => availableBundles.filter((bundle) => resolveBundleKey(bundle) !== 'freemium'),
    [availableBundles],
  );

  // Derived, not stored: a second source of truth would drift from the form's
  // packageId. Note this is strictly "freemium is selected" — an empty form is
  // *no* choice, not a deferred one, so the box starts unchecked and the client
  // has to say which of the two they mean.
  const isChoiceDeferred = Boolean(freemiumBundle) && selectedBundleId === freemiumBundle?.packageId;

  const selectBundle = (bundle?: ApiSchema<'ActivationBundle'>) => {
    if (!bundle?.available || inActivePeriod) return;

    setFormValues((prev) => ({ ...(prev as Record<string, unknown>), packageId: bundle.packageId }));
    setMirrorData?.({ packageId: bundle.packageId, bundle: bundle.identifier });
  };

  // Unchecking "I will choose later" puts the form back to no choice at all,
  // which the activation guard rejects — deliberately. The client has to make
  // one of the two decisions rather than fall into freemium by inaction.
  const clearSelection = () => {
    if (inActivePeriod) return;

    setFormValues((prev) => ({ ...(prev as Record<string, unknown>), packageId: undefined }));
    setMirrorData?.({ packageId: undefined, bundle: undefined });
  };

  // Tab change: flip the mirror flag; the parent's activation-services SWR
  // picks it up on the next render and refetches the opposite sibling set.
  // We deliberately do NOT reset packageId here — the onDataChanged callback
  // in JobAddV2Form re-resolves selection by identifier, which transfers the
  // user's tier choice (standard/plus/pro) across sibling swaps for free.
  const switchTab = (nextWithoutHire: boolean) => {
    if (inActivePeriod || nextWithoutHire === withoutHire) return;
    setMirrorData?.({ withoutHire: nextWithoutHire }, false, true);
  };


  return (
    <div className="grid gap-5">
      <div
        role="tablist"
        aria-label={t('job.bundle.selection.tab.we_recruit.label')}
        className="inline-flex self-start rounded-full border border-input/70 bg-surface p-1 text-sm"
      >
        {([
          { key: 'we', value: false, label: 'job.bundle.selection.tab.we_recruit.label', sub: 'job.bundle.selection.tab.we_recruit.sub' },
          { key: 'you', value: true, label: 'job.bundle.selection.tab.you_recruit.label', sub: 'job.bundle.selection.tab.you_recruit.sub' },
        ] as const).map((tab) => {
          const isActive = withoutHire === tab.value;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              disabled={inActivePeriod}
              onClick={() => switchTab(tab.value)}
              className={`flex flex-col items-center rounded-full px-5 py-2 leading-tight transition-colors ${
                isActive
                  ? 'bg-secondary text-surface'
                  : 'text-ink-medium hover:text-ink'
              } ${inActivePeriod ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
            >
              <span className="text-sm font-medium">{t(tab.label)}</span>
              <span className={`text-xs ${isActive ? 'opacity-90' : 'opacity-70'}`}>{t(tab.sub)}</span>
            </button>
          );
        })}
      </div>

      {!withoutHire && (
        <div className="flex items-center gap-3 rounded-2xl bg-secondary px-5 py-4 text-surface">
          <span className="shrink-0 rounded-full bg-warning/90 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-ink">
            {t('job.bundle.selection.we_recruit.banner.badge')}
          </span>
          <span className="text-sm font-semibold">
            {t('job.bundle.selection.we_recruit.banner.title')}
          </span>
        </div>
      )}

      <div className="grid gap-5">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {paidBundles.map((bundle) => (
            <ActivationBundleCardV2
              key={bundle.packageId}
              bundle={bundle}
              isSelected={selectedBundleId === bundle.packageId}
              disabled={inActivePeriod}
              onSelect={(nextBundle) => selectBundle(nextBundle)}
              t={t}
              withoutHire={withoutHire}
            />
          ))}
        </div>

        {/* Hidden when the offered set has no freemium bundle: there would be
            nothing for the checkbox to submit, and the paid cards are then the
            only way forward anyway. */}
        {freemiumBundle && (
          <label
            htmlFor="job-add-defer-bundle-choice"
            className={`inline-flex items-center gap-2 text-sm ${
              inActivePeriod ? 'cursor-default text-ink-medium' : 'cursor-pointer text-ink-medium'
            }`}
          >
            <input
              id="job-add-defer-bundle-choice"
              type="checkbox"
              className="size-4 shrink-0 rounded border-input/70 text-secondary accent-secondary focus-visible:ring-[3px] focus-visible:ring-secondary/20 disabled:opacity-60"
              checked={isChoiceDeferred}
              disabled={inActivePeriod}
              // Both directions are real choices: ticking it selects freemium,
              // clearing it returns the form to "nothing picked", which blocks
              // activation until the client decides.
              onChange={() => (isChoiceDeferred ? clearSelection() : selectBundle(freemiumBundle))}
              aria-describedby={hasMissingSelectionError ? 'job-add-bundle-error' : undefined}
            />
            <span>{t('job.add.bundle.choose-later')}</span>
          </label>
        )}

        {/* Shown only after an activation attempt with nothing picked — the
            section is not pre-marked as invalid on a form the client has not
            submitted yet. */}
        {hasMissingSelectionError && (
          <p id="job-add-bundle-error" role="alert" className="text-sm font-medium text-error">
            {t('job.add.bundle.required.error')}
          </p>
        )}
      </div>
    </div>
  );
};
